import { useMemo, useState } from 'react';
import { Button, Stepper, Tag } from '../ds';
import { Sheet, SheetRow } from './Sheet';
import { DateField, TimeField } from './TimeField';
import { DiaperToggles } from './DiaperToggles';
import { Eyebrow, Mono } from './primitives';
import { useToast } from './Toast';
import * as api from '../lib/db';
import {
  FEED_SOURCES,
  entryToastMessage,
  feedDefaultAmount,
  feedQtyLabel,
  feedStep,
  feedUnit,
  isBreast,
} from '../lib/labels';
import { formatDurationJa, minutesBetween } from '../lib/time';
import { resolveSleepEnd } from '../lib/entries';
import type { Entry, FeedSource, Member } from '../lib/types';

/**
 * Edit-entry sheet — りな's 「1回登録すると修正ができない」 fix.
 *
 * Opens from a tap on any timeline row, prefilled with the entry's values.
 * Type and child stay fixed (a mis-typed entry is deleted and re-added);
 * either parent can correct either parent's entry, and the correction is
 * recorded on the activity feed under the editor's name.
 */

export function EditSheet({
  entry,
  familyId,
  me,
  onClose,
}: {
  entry: Entry | null;
  familyId: string;
  me: Member;
  onClose: () => void;
}) {
  const toast = useToast();

  const [lastId, setLastId] = useState<string | null>(null);
  const [at, setAt] = useState<Date>(() => new Date());
  const [source, setSource] = useState<FeedSource>('right');
  const [amount, setAmount] = useState(0);
  const [pee, setPee] = useState(false);
  const [poop, setPoop] = useState(false);
  const [grams, setGrams] = useState(0);
  const [endAt, setEndAt] = useState<Date | null>(null);
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Prefill whenever a different entry is opened.
  if (entry && entry.id !== lastId) {
    setLastId(entry.id);
    setAt(new Date(entry.at));
    setConfirmingDelete(false);
    setError(null);
    if (entry.type === 'feed') {
      setSource(entry.source);
      setAmount(entry.amount);
    } else if (entry.type === 'diaper') {
      setPee(entry.pee);
      setPoop(entry.poop);
    } else if (entry.type === 'sleep') {
      setEndAt(entry.endAt ? new Date(entry.endAt) : null);
    } else {
      setGrams(entry.grams);
    }
  }

  /**
   * Correcting 右→左 keeps the minutes (the wrong-breast fix is the common
   * case); crossing between a breast and a bottle changes the unit, so the
   * amount resets to that source's default instead of reading 6分 as 6ml.
   */
  const pickSource = (next: FeedSource) => {
    if (isBreast(next) !== isBreast(source)) setAmount(feedDefaultAmount(next));
    setSource(next);
  };

  /**
   * The end's day is explicit via the date field; the same-day midnight
   * convenience (23:40→06:20) lives in resolveSleepEnd, shared with the add
   * sheet. A result that is still not after the start is invalid — surfaced
   * as a warning, and save stays disabled.
   */
  const resolvedEnd = useMemo(() => {
    if (!endAt) return null;
    return resolveSleepEnd(at, endAt);
  }, [endAt, at]);
  const sleepEndInvalid = entry?.type === 'sleep' && endAt !== null && resolvedEnd === null;

  if (!entry) return null;

  const buildInput = (): api.NewEntry =>
    entry.type === 'feed'
      ? { type: 'feed', childId: entry.childId, at, source, amount }
      : entry.type === 'diaper'
        ? { type: 'diaper', childId: entry.childId, at, pee, poop }
        : entry.type === 'sleep'
          ? { type: 'sleep', childId: entry.childId, at, endAt: resolvedEnd }
          : { type: 'solid', childId: entry.childId, at, grams };

  const save = async () => {
    setSaving(true);
    setError(null);
    const input = buildInput();
    try {
      await api.editEntry(familyId, entry.id, me, input);
      toast.show({ title: '修正しました', message: entryToastMessage(input) });
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : '保存できませんでした。');
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    setSaving(true);
    setError(null);
    try {
      await api.removeEntry(familyId, entry, me);
      toast.show({ title: '削除しました', message: entryToastMessage(entry) });
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : '削除できませんでした。');
      setSaving(false);
    }
  };

  return (
    <Sheet
      open
      onClose={onClose}
      eyebrow="Edit entry"
      title="記録を修正"
      footer={
        <>
          {error ? (
            <div
              style={{
                fontSize: 'var(--text-xs)',
                color: 'var(--danger)',
                marginBottom: 8,
                lineHeight: 'var(--leading-cjk)',
              }}
            >
              {error}
            </div>
          ) : null}
          <Button
            variant="primary"
            fullWidth
            iconStart="Check"
            size="lg"
            disabled={saving || sleepEndInvalid}
            onClick={save}
          >
            {saving ? '保存中…' : '保存する · Save'}
          </Button>
        </>
      }
    >
      {/* ---- 授乳 ---- */}
      {entry.type === 'feed' ? (
        <>
          <Eyebrow style={{ marginBottom: 9 }}>授乳の種類 · Source</Eyebrow>
          <div style={{ display: 'flex', gap: 7, flexWrap: 'wrap', marginBottom: 6 }}>
            {FEED_SOURCES.map((f) => (
              <Tag key={f.key} selected={source === f.key} onClick={() => pickSource(f.key)}>
                {f.label}
              </Tag>
            ))}
          </div>
          <SheetRow label={<Eyebrow>日時 · Time</Eyebrow>}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <DateField value={at} onChange={setAt} />
              <TimeField value={at} onChange={setAt} />
            </div>
          </SheetRow>
          <SheetRow label={<Eyebrow>{feedQtyLabel(source)}</Eyebrow>}>
            <Stepper
              value={amount}
              onChange={setAmount}
              step={feedStep(source)}
              unit={feedUnit(source)}
              min={0}
              aria-label={feedQtyLabel(source)}
            />
          </SheetRow>
        </>
      ) : null}

      {/* ---- おむつ ---- */}
      {entry.type === 'diaper' ? (
        <>
          <Eyebrow style={{ marginBottom: 9 }}>内容 · Contents</Eyebrow>
          <DiaperToggles
            pee={pee}
            poop={poop}
            onChange={(next) => {
              setPee(next.pee);
              setPoop(next.poop);
            }}
          />
          <SheetRow label={<Eyebrow>日時 · Time</Eyebrow>}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <DateField value={at} onChange={setAt} />
              <TimeField value={at} onChange={setAt} />
            </div>
          </SheetRow>
        </>
      ) : null}

      {/* ---- ねんね ---- */}
      {entry.type === 'sleep' ? (
        <>
          <SheetRow
            first
            label={
              <div>
                <div style={{ fontWeight: 700, fontSize: 'var(--text-sm)' }}>はじまり</div>
                <div style={{ fontSize: 11, color: 'var(--text-subtle)' }}>Start</div>
              </div>
            }
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <DateField value={at} onChange={setAt} />
              <TimeField value={at} onChange={setAt} />
            </div>
          </SheetRow>
          <SheetRow
            label={
              <div>
                <div style={{ fontWeight: 700, fontSize: 'var(--text-sm)' }}>おわり</div>
                <div style={{ fontSize: 11, color: 'var(--text-subtle)' }}>End</div>
              </div>
            }
          >
            {endAt ? (
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <DateField value={endAt} onChange={setEndAt} />
                <TimeField value={endAt} onChange={setEndAt} />
              </div>
            ) : (
              <Mono style={{ fontSize: 'var(--text-md)', color: 'var(--text-subtle)' }}>
                — 記録中
              </Mono>
            )}
          </SheetRow>
          {sleepEndInvalid ? (
            <SheetRow label={<Eyebrow>長さ · Duration</Eyebrow>}>
              <span className="cjk" style={{ fontSize: 'var(--text-xs)', color: 'var(--danger)' }}>
                おわりがはじまりより前です
              </span>
            </SheetRow>
          ) : resolvedEnd ? (
            <SheetRow label={<Eyebrow>長さ · Duration</Eyebrow>}>
              <Mono style={{ fontSize: 'var(--text-md)' }}>
                {formatDurationJa(minutesBetween(at, resolvedEnd))}
              </Mono>
            </SheetRow>
          ) : null}
        </>
      ) : null}

      {/* ---- 離乳食 ---- */}
      {entry.type === 'solid' ? (
        <>
          <SheetRow label={<Eyebrow>日時 · Time</Eyebrow>} first>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <DateField value={at} onChange={setAt} />
              <TimeField value={at} onChange={setAt} />
            </div>
          </SheetRow>
          <SheetRow label={<Eyebrow>量 · Amount</Eyebrow>}>
            <Stepper value={grams} onChange={setGrams} step={10} unit="g" min={0} aria-label="量" />
          </SheetRow>
        </>
      ) : null}

      {/* ---- delete ---- */}
      <div style={{ margin: '16px 0 8px', borderTop: '1px solid var(--border)', paddingTop: 14 }}>
        {confirmingDelete ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <span
              className="cjk"
              style={{ flex: 1, minWidth: 0, fontSize: 'var(--text-sm)', color: 'var(--text-muted)' }}
            >
              本当に削除しますか？
            </span>
            <Button variant="ghost" size="sm" onClick={() => setConfirmingDelete(false)}>
              やめる
            </Button>
            <Button variant="danger" size="sm" iconStart="Trash2" disabled={saving} onClick={remove}>
              削除する
            </Button>
          </div>
        ) : (
          <Button
            variant="ghost"
            size="sm"
            iconStart="Trash2"
            onClick={() => setConfirmingDelete(true)}
            style={{ color: 'var(--danger)' }}
          >
            この記録を削除
          </Button>
        )}
      </div>
    </Sheet>
  );
}
